import { useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'
import { saveWithConflictCheck } from '../../../lib/conflictResolver'

export const useMealPlan = (weekStart, weekEnd) => {
  const { household } = useAuth()
  const qc  = useQueryClient()
  const key = ['meal-plan', household?.id, weekStart]

  const query = useQuery({
    queryKey: key,
    enabled:  !!household?.id && !!weekStart,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('meal_plan_slots')
        .select('id, date, slot_name, recipe_id, custom_name, recipes(id, title, cook_time_minutes)')
        .eq('household_id', household.id)
        .gte('date', weekStart)
        .lte('date', weekEnd)
      if (error) throw error
      return data ?? []
    },
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: key })

  const upsertSlot = async ({ date, slot_name, recipe_id, custom_name, existingSlot }) => {
    const dateStr = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd')
    const updates = { recipe_id: recipe_id || null, custom_name: custom_name || null }

    if (existingSlot?.id && existingSlot?.updated_at) {
      const result = await saveWithConflictCheck('meal_plan_slots', existingSlot.id, updates, existingSlot.updated_at)
      if (result.conflict) {
        // Refetch silently — someone else already changed the slot
        invalidate()
        return
      }
    } else {
      const { error } = await supabase.from('meal_plan_slots').upsert(
        { household_id: household.id, date: dateStr, slot_name, ...updates },
        { onConflict: 'household_id,date,slot_name' },
      )
      if (error) throw error
    }
    invalidate()
  }

  const clearSlot = async ({ date, slot_name }) => {
    const dateStr = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd')
    const { error } = await supabase
      .from('meal_plan_slots')
      .delete()
      .eq('household_id', household.id)
      .eq('date', dateStr)
      .eq('slot_name', slot_name)
    if (error) throw error
    invalidate()
  }

  return { ...query, upsertSlot, clearSlot }
}
